// src/pages/MaterialReception/MaterialReception.jsx
import React, { useEffect, useState, useMemo, useContext } from "react";
import { FaSearch, FaTimes, FaRegTrashAlt } from "react-icons/fa";
import {
  MdLocalLaundryService,
  MdLocalHospital,
  MdDoneAll,
  MdHourglassTop,
} from "react-icons/md";
import { GiBrokenBone } from "react-icons/gi";
import { BsQrCodeScan } from "react-icons/bs";
import {
  FiAlertTriangle,
  FiCheckCircle,
  FiLock,
  FiXCircle,
} from "react-icons/fi";
import Tooltip from "@material-ui/core/Tooltip";
import { toast } from "react-toastify";
import request from "../../utils/request";
import PageContainer from "../../components/PageContainer/PageContainer";
import styles from "./MaterialReception.module.css";
import t from "../../utils/translation";
import LocalizationContext from "../../utils/LocalizationContext";
import BarcodeReader from "react-barcode-reader";

const DESTS = [
  {
    key: "wash",
    icon: MdLocalLaundryService,
    color: "blue",
    label: t("BlankEntry247"),
  },
  {
    key: "ster",
    icon: MdLocalHospital,
    color: "green",
    label: t("BlankEntry248"),
  },
  { key: "broken", icon: FiXCircle, color: "error", label: t("BlankEntry212") },
  {
    key: "lost",
    icon: FiAlertTriangle,
    color: "orange",
    label: t("BlankEntry200"),
  },
];
const PKG_STATUS = {
  ACCEPTED: 8,
  REJECTED: 9,
  MISSING: 11,
  RECEPTION_PARCIAL: 13,
  RECEPTION: 12,
};
const MAT_STATUS = { OK: 1, BROKEN: 13, LOST: 14 };

export default function MaterialReception() {
  const [dispatches, setDispatches] = useState([]);
  const [search, setSearch] = useState("");
  const [focus, setFocus] = useState(false);
  const [addedIds, setAddedIds] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [plan, setPlan] = useState({});
  const [user, setUser] = useState("");
  const [note, setNote] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [scanOn, setScanOn] = useState(false);
  const [selectedMaterials, setSelectedMaterials] = useState([]);
  const [currentDest, setCurrentDest] = useState("wash");
  const locale = useContext(LocalizationContext);

  /* ---------- fetch dispatches ---------- */
  const fetchDispatches = async () => {
    try {
      const { data } = await request().get(
        "/api/reception/pending-dispatches?dispatchStatus=9999"
      );
      setDispatches(data.dispatches);
    } catch {
      toast.error("Failed to load dispatches");
    }
  };
  useEffect(() => {
    fetchDispatches();
  }, []);

  /* ---------- plan helpers ---------- */
  const initialRow = (m, pkgStatus) => {
    const statuses = m.MaterialStatuses ?? [];
    // const total = statuses.reduce((sum, s) => sum + (s.quantity ?? 0), 0);
    let row = { wash: 0, ster: 0, broken: 0, lost: 0 };

    statuses.forEach(({ status, quantity }) => {
      if (status === 13) row.broken += quantity ?? 0;
      else if (status === 14) row.lost += quantity ?? 0;
      else row.wash += quantity ?? 0;
    });

    const filled = Object.values(row).reduce((a, b) => a + b, 0);
    if (filled === 0) row.wash = m.restQuantity ?? 1;

    if (pkgStatus === PKG_STATUS.MISSING) {
      row = { wash: 0, ster: 0, broken: 0, lost: m.quantity ?? 1 };
    }

    return row;
  };

  const getRow = (k, m, pkgStatus) => {
    const existing = plan[k];
    if (existing) return existing;

    const row = initialRow(m, pkgStatus);
    saveRow(k, row);
    return row;
  };

  const saveRow = (k, row) => setPlan((p) => ({ ...p, [k]: row }));

  const setVal = (k, dest, val, max) => {
    const n = Math.max(0, Number(val));
    const currentRow = plan[k] || {};
    const updatedRow = { ...currentRow, [dest]: n };

    const total = Object.values(updatedRow).reduce(
      (sum, v) => sum + Number(v),
      0
    );
    if (total > max) return;

    saveRow(k, updatedRow);
  };

  /* ---------- search/filter ---------- */
  const searchResults = useMemo(() => {
    if (!Array.isArray(dispatches)) return [];
    const term = search.trim().toLowerCase();
    return dispatches.filter(
      (d) =>
        !addedIds.includes(d.dispatchId) &&
        (d.dispatchNumber.toString().includes(term) ||
          d.packages.some(
            (p) =>
              p.packageNumber.toString().includes(term) ||
              p.packageName.toLowerCase().includes(term)
          ))
    );
  }, [dispatches, search, addedIds]);

  const addedDispatches = useMemo(
    () => dispatches.filter((d) => addedIds.includes(d.dispatchId)),
    [dispatches, addedIds]
  );
  const activeDisp = dispatches.find((d) => d.dispatchId === activeId) || null;

  const addDispatch = (id) => {
    setAddedIds((p) => [...p, id]);
    setActiveId(id);
  };
  const removeDispatch = (id) => {
    setAddedIds((p) => p.filter((x) => x !== id));
    if (activeId === id) setActiveId(null);
  };

  /* ---------- move helpers ---------- */
  const moveAllPackages = (disp, dest) => {
    if (!disp) return;
    disp.packages.forEach((pkg) => {
      if (pkg.packageStatus === PKG_STATUS.MISSING) return;
      pkg.materials.forEach((m) => {
        if (
          m.materialStatus === MAT_STATUS.BROKEN ||
          m.materialStatus === MAT_STATUS.LOST
        )
          return;

        saveRow(`${pkg.packageId}-${m.materialId}`, {
          wash: 0,
          ster: 0,
          broken: 0,
          lost: 0,
          [dest]: m.restQuantity,
        });
      });
    });
  };
  const moveAll = (pkg, dest) => {
    if (pkg.packageStatus === PKG_STATUS.MISSING) return;
    pkg.materials.forEach((m) => {
      if (
        m.materialStatus === MAT_STATUS.BROKEN ||
        m.materialStatus === MAT_STATUS.LOST
      )
        return;

      saveRow(`${pkg.packageId}-${m.materialId}`, {
        wash: 0,
        ster: 0,
        broken: 0,
        lost: 0,
        [dest]: m.restQuantity,
      });
    });
  };

  /* ---------- scan ---------- */
  const onHandleScan = (serial) => {
    if (!activeDisp) return;
    const mats = activeDisp.packages
      .filter((p) => p.packageStatus !== PKG_STATUS.MISSING)
      .flatMap((p) => p.materials);
    const itm = mats.find((m) => m.serial === serial);
    if (!itm) {
      toast.error(t("BlankEntry215"));
      return;
    }
    const exists = selectedMaterials.find((m) => m.serial === serial);
    if (exists) {
      setSelectedMaterials((s) => s.filter((m) => m.serial !== serial));
      toast.success(t("MaterialRemoved", locale) || "Material removed");
    } else {
      setSelectedMaterials((s) => [...s, { ...itm, dest: currentDest }]);
      toast.success(t("MaterialAdded", locale) || "Material added");
    }
  };

  const confirmScan = () => {
    if (!activeDisp) return;
    selectedMaterials.forEach((sel) => {
      const pkg = activeDisp.packages.find(
        (p) =>
          p.packageStatus !== PKG_STATUS.MISSING &&
          p.materials.some((m) => m.serial === sel.serial)
      );
      if (!pkg) return;
      const mat = pkg.materials.find((m) => m.serial === sel.serial);
      if (!mat) return;
      saveRow(`${pkg.packageId}-${mat.materialId}`, {
        wash: 0,
        ster: 0,
        broken: 0,
        lost: 0,
        [sel.dest]: 1,
      });
    });
    setSelectedMaterials([]);
    setScanOn(false);
    toast.success(t("BlankEntry227"));
  };

  /* ---------- confirm reception ---------- */
  const confirm = async () => {
    const errs = [];
    if (!addedIds.length) errs.push(t("BlankEntry228"));
    if (!user.trim()) errs.push(t("BlankEntry187"));
    const hasMat = addedDispatches.some((d) =>
      d.packages.some((pkg) =>
        pkg.materials.some((m) => {
          const row = getRow(`${pkg.packageId}-${m.materialId}`, m);
          return Object.values(row).some((q) => Number(q) > 0);
        })
      )
    );

    if (!hasMat) errs.push(t("BlankEntry229"));
    if (errs.length) {
      errs.forEach((m) => toast.info(m));
      return;
    }

    const mapStatus = { wash: 1, ster: 4, broken: 13, lost: 14 };

    const dispatchesPayload = addedDispatches
      .map((d) => {
        const Materials = [];
        d.packages
          .filter(
            (pkg) =>
              pkg.packageStatus === PKG_STATUS.ACCEPTED ||
              pkg.packageStatus === PKG_STATUS.RECEPTION_PARCIAL ||
              pkg.packageStatus === PKG_STATUS.REJECTED
          )
          .forEach((pkg) => {
            pkg.materials.forEach((m) => {
              const row = getRow(`${pkg.packageId}-${m.materialId}`, m);
              Object.entries(row).forEach(([dest, q]) => {
                const qty = Number(q);
                if (qty)
                  Materials.push({
                    PackageId: pkg.packageId,
                    MaterialId: m.materialId,
                    Quantity: qty,
                    MaterialStatus: mapStatus[dest],
                  });
              });
            });
          });
        return Materials.length
          ? { DispatchId: d.dispatchId, Materials }
          : null;
      })
      .filter(Boolean);

    console.log(dispatchesPayload);

    if (!dispatchesPayload.length) {
      toast.info(t("BlankEntry230"));
      return;
    }

    try {
      setIsSubmitting(true);
      await request().post("/api/reception", {
        observations: note.trim(),
        shippingResponsible: user.trim(),
        dispatches: dispatchesPayload,
      });
      toast.success(t("BlankEntry231"));
      setAddedIds([]);
      setActiveId(null);
      setPlan({});
      setUser("");
      setNote("");
      fetchDispatches();
    } catch {
      toast.error("An error occurred while saving the reception.");
    } finally {
      setIsSubmitting(false);
    }
  };

  /* ---------- UI helpers ---------- */

  const badgeStatus = (st) => {
    switch (st) {
      case PKG_STATUS.MISSING:
        return (
          <span className={`${styles.badge} ${styles.orange}`}>
            <FiXCircle size={12} /> {t("BlankEntry200")}
          </span>
        );
      case PKG_STATUS.REJECTED:
        return (
          <span className={`${styles.badge} ${styles.red}`}>
            <FiXCircle size={12} /> {t("BlankEntry127")}
          </span>
        );
      case PKG_STATUS.ACCEPTED:
        return (
          <span className={`${styles.badge} ${styles.green}`}>
            <FiCheckCircle size={12} /> {t("BlankEntry232")}
          </span>
        );
      case PKG_STATUS.RECEPTION:
        return (
          <span className={`${styles.badge} ${styles.darkGreen}`}>
            <MdDoneAll size={12} /> {t("BlankEntry241")}
          </span>
        );
      case PKG_STATUS.RECEPTION_PARCIAL:
        return (
          <span className={`${styles.badge} ${styles.yellow}`}>
            <MdHourglassTop size={12} /> {t("BlankEntry242")}
          </span>
        );

      default:
        return null;
    }
  };

  const countPkgMat = (pkg) =>
    pkg.materials.reduce((s, m) => s + (m.quantity ?? 1), 0);
  const countDisp = (d) => d.packages.reduce((s, p) => s + countPkgMat(p), 0);

  const handleInputChange = (e, { key, catKey, max, isSerial, material }) => {
    const raw = e.target.value;

    if (raw === "") {
      saveRow(key, { ...plan[key], [catKey]: "" });
      return;
    }

    const parsed = parseInt(raw, 10);
    if (isNaN(parsed) || parsed < 0) return;

    const totalQty = material.quantity ?? 1;
    const behavesLikeSerial = isSerial || totalQty === 1;

    // --- Seriado o cantidad 1 ---
    if (behavesLikeSerial) {
      if (parsed > 1) return;

      if (parsed === 1) {
        const updated = Object.fromEntries(
          DESTS.map((d) => [d.key, d.key === catKey ? 1 : 0])
        );
        saveRow(key, updated);
      } else {
        saveRow(key, { ...plan[key], [catKey]: 0 });
      }
      return;
    }

    // --- No seriado, cantidad > 1 ---
    const currentRow = { ...plan[key] };
    const alreadyAssigned = Object.entries(currentRow)
      .filter(([k]) => k !== catKey)
      .reduce((sum, [_, val]) => sum + (Number(val) || 0), 0);

    const remaining = totalQty - alreadyAssigned;

    if (parsed > remaining) {
      toast.info(
        t("BlankEntry244") || "No puede asignar más que lo disponible."
      );
      return;
    }

    saveRow(key, { ...currentRow, [catKey]: parsed });
  };

  const handleBlur = ({ key, catKey }) => {
    const row = plan[key];
    if (row?.[catKey] === "") {
      saveRow(key, { ...row, [catKey]: 0 });
    }
  };

  /* ---------- render ---------- */
  return (
    <PageContainer
      title="Material Reception"
      category="Reception"
      categoryId={5}
      backUrl="/appreceptionmenu"
    >
      <BarcodeReader onScan={onHandleScan} />
      <div className={styles.containerLayout}>
        {/* ------------ LEFT COLUMN ------------ */}
        <aside className={styles.leftColumn}>
          {/* search */}
          <div className={styles.searchWrapper}>
            <FaSearch className={styles.searchIcon} />
            <input
              className={styles.searchInput}
              placeholder={t("BlankEntry233")}
              value={search}
              onFocus={() => setFocus(true)}
              onBlur={() => setTimeout(() => setFocus(false), 120)}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          {focus && (
            <div className={styles.resultsBox}>
              {searchResults.length ? (
                searchResults.map((d) => (
                  <button
                    key={d.dispatchId}
                    className={styles.resultItem}
                    onMouseDown={() => {
                      addDispatch(d.dispatchId);
                      setSearch("");
                    }}
                  >
                    <div>
                      <strong>{d.dispatchNumber}</strong>
                    </div>
                    <div className={styles.resultPackages}>
                      {d.packages.map((p) => (
                        <div key={p.packageId}>
                          <span className={styles.pkgNum}>
                            {p.packageNumber}
                          </span>{" "}
                          – {p.packageName}
                        </div>
                      ))}
                    </div>
                  </button>
                ))
              ) : (
                <p className={styles.emptyText}>{t("BlankEntry116")}</p>
              )}
            </div>
          )}

          <h2 className={styles.leftColumnTitle}>{t("BlankEntry234")}</h2>
          <div className={styles.leftPackagesScroll}>
            {addedDispatches.length ? (
              addedDispatches.map((d) => (
                <div
                  key={d.dispatchId}
                  className={`${styles.dispatchCard} ${
                    activeId === d.dispatchId ? styles.activeCard : ""
                  }`}
                  onClick={() => setActiveId(d.dispatchId)}
                >
                  <div className={styles.dispatchCardHeader}>
                    <span>{d.dispatchNumber}</span>
                    <FaTimes
                      className={styles.removeIcon}
                      onClick={(e) => {
                        e.stopPropagation();
                        removeDispatch(d.dispatchId);
                      }}
                    />
                  </div>
                  <small>
                    {d.packages.length} pkg · {countDisp(d)}{" "}
                    {t("MaterialsSpan")}
                  </small>
                </div>
              ))
            ) : (
              <p className={styles.emptyText}>None</p>
            )}
          </div>

          {/* form */}
          <div className={styles.dispatchForm}>
            <label>
              {t("BlankEntry173") || "Shipping Responsible"}
              <span style={{ color: "red" }}>*</span>
            </label>
            <input
              value={user}
              onChange={(e) => setUser(e.target.value)}
              placeholder={t("BlankEntry173") || "Shipping Responsible"}
            />
            <div className={styles.observations}>
              <label htmlFor="obs">{t("ObservationSpan")}</label>
              <textarea
                id="obs"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder={t("ObservationSpan")}
              />
            </div>
          </div>

          <button
            className={styles.confirmButton}
            onClick={confirm}
            disabled={isSubmitting}
          >
            {isSubmitting ? `${t("BlankEntry216")}...` : t("ConfirmButton")}
          </button>
        </aside>

        {/* ------------ MAIN CONTENT ------------ */}
        <section className={styles.mainContent}>
          {/* scan modal */}
          {scanOn && (
            <div
              className={styles.modalOverlay}
              onClick={() => setScanOn(false)}
            >
              <div
                className={styles.scanModal}
                onClick={(e) => e.stopPropagation()}
              >
                <header className={styles.modalHeader}>
                  <h3>{t("BlankEntry245")}</h3>

                  <button
                    className={styles.closeModal}
                    onClick={() => setScanOn(false)}
                  >
                    ×
                  </button>
                </header>
                <div className={styles.modalButtonContainer}>
                  {DESTS.filter(({ key }) =>
                    ["wash", "ster", "broken"].includes(key)
                  ).map(({ key, icon: Icon, color, label }) => (
                    <div
                      key={key}
                      className={`${styles.modalBtn} ${styles[color]} ${
                        currentDest === key
                          ? styles.modalBtnActive
                          : styles.modalBtnInactive
                      }`}
                      onClick={() => setCurrentDest(key)}
                    >
                      <Icon className={styles.icon} />
                      <span className={styles.destLabel}>{label}</span>
                    </div>
                  ))}
                </div>

                <ul className={styles.materialList}>
                  {selectedMaterials.length ? (
                    selectedMaterials.map((m) => {
                      const dInfo = DESTS.find((d) => d.key === m.dest);
                      const DestIcon = dInfo.icon;
                      return (
                        <li key={m.serial} className={styles.materialRow}>
                          <span>{m.materialName}</span>
                          {m.serial && (
                            <span className={styles.serialBadge}>
                              {m.serial}
                            </span>
                          )}
                          <span
                            className={`${styles.destChip} ${
                              styles[dInfo.color]
                            }`}
                          >
                            <DestIcon className={styles.destIcon} />
                            {dInfo.label}
                          </span>
                        </li>
                      );
                    })
                  ) : (
                    <li className={styles.emptyText}>{t("BlankEntry235")}</li>
                  )}
                </ul>
                <footer className={styles.modalFooter}>
                  <button className={styles.modalConfirm} onClick={confirmScan}>
                    {t("ConfirmButton")}
                  </button>
                </footer>
              </div>
            </div>
          )}

          {!activeDisp ? (
            <p className={styles.emptyText}>{t("BlankEntry236")}</p>
          ) : (
            <div className={styles.dispatchBlock}>
              <div className={styles.dispatchHeader}>
                <h2 className={styles.dispatchTitle}>
                  {t("BlankEntry166")} {activeDisp.dispatchNumber}
                  <span className={styles.badge}>
                    {activeDisp.packages.length} pkg · {countDisp(activeDisp)}{" "}
                    {t("MaterialsSpan")}
                  </span>
                </h2>
                <div className={styles.buttonContainer}>
                  <button
                    className={styles.scanButton}
                    onClick={() => setScanOn(true)}
                  >
                    <BsQrCodeScan /> {t("BlankEntry237")}
                  </button>
                  {DESTS.map(({ key, color, icon: Icon, label }) => (
                    <div
                      className={`${styles.button} ${styles[color]}`}
                      onClick={() => moveAllPackages(activeDisp, key)}
                    >
                      <Icon className={styles.icon} />
                      <span className={styles.destLabel}>{label}</span>
                    </div>
                  ))}
                </div>
              </div>

              {activeDisp.packages.map((pkg) => {
                const isMissing = pkg.packageStatus === PKG_STATUS.MISSING;
                return (
                  <div
                    key={pkg.packageId}
                    className={`${styles.packageCard} ${
                      [PKG_STATUS.MISSING, PKG_STATUS.RECEPTION].includes(
                        pkg.packageStatus
                      )
                        ? styles.disabledPackageCard
                        : ""
                    }`}
                  >
                    <header className={styles.packageHeader}>
                      <h3>
                        {pkg.packageNumber} – {pkg.packageName}
                        <span className={styles.packageMeta}>
                          {countPkgMat(pkg)} {t("MaterialsSpan")}
                        </span>
                      </h3>
                      {badgeStatus(pkg.packageStatus)}
                    </header>

                    <table className={styles.materialTable}>
                      <thead>
                        <tr>
                          <th>{t("MaterialSpan")}</th>
                          <th>{t("QuantitySpan")}</th>
                          <th>{t("AvailableSpan")}</th>
                          {DESTS.map(({ key, icon: Icon, color }) => (
                            <th key={key} className={styles.centered}>
                              <div
                                className={`${styles.button} ${styles[color]}`}
                                onClick={() => !isMissing && moveAll(pkg, key)}
                              >
                                <Icon className={styles.icon} />
                              </div>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {pkg.materials.map((m) => {
                          const rowKey = `${pkg.packageId}-${m.materialId}`;
                          const max = m.quantity ?? 1;
                          const row = getRow(rowKey, m, pkg.packageStatus);

                          const disabledMat =
                            m.MaterialStatuses?.some(
                              (s) => s.status === 13 || s.status === 14
                            ) || isMissing;

                          return (
                            <tr
                              key={m.materialId}
                              className={
                                m.restQuantity === 0 ? styles.completedRow : ""
                              }
                            >
                              <td>
                                {m.materialName}
                                {m.serial && (
                                  <span className={styles.serialBadge}>
                                    {m.serial}
                                  </span>
                                )}
                              </td>
                              <td>{max}</td>
                              <td>{m.restQuantity}</td>
                              {DESTS.map(({ key }) => {
                                const isLocked = disabledMat && row[key] > 0;
                                return (
                                  <td key={key}>
                                    <div className={styles.inputWrapper}>
                                      <input
                                        disabled={disabledMat}
                                        type="text"
                                        min={0}
                                        max={m.restQuantity}
                                        value={row[key]}
                                        className={`${styles.qtyInput} ${
                                          isLocked ? styles.lockedInput : ""
                                        }`}
                                        onChange={(e) =>
                                          handleInputChange(e, {
                                            key: rowKey,
                                            catKey: key,
                                            max: m.restQuantity,
                                            isSerial: !!m.serial,
                                            material: m,
                                          })
                                        }
                                        onBlur={() =>
                                          handleBlur({
                                            key: rowKey,
                                            catKey: key,
                                          })
                                        }
                                      />
                                      {isLocked && row[key] > 0 && (
                                        <FiLock
                                          className={styles.inputIcon}
                                          size={12}
                                        />
                                      )}
                                    </div>
                                  </td>
                                );
                              })}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </div>
    </PageContainer>
  );
}
